import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { Drawer, DrawerBadge } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { IntakeRenderer } from '@helios/ui/intake';
import type { IntakeQuestionnaire, IntakeAnswers } from '@helios/ui/intake';

// ─── Types ───

interface UpdateIntakeFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  questionnaire: IntakeQuestionnaire;
  initialAnswers: IntakeAnswers;
  validationSchema: string | null;
  banner: { type: 'info' | 'danger'; message: string } | null;
  onSave: (answers: IntakeAnswers) => void;
}

// ─── Banner ───

function IntakeBanner({ type, message }: { type: 'info' | 'danger'; message: string }) {
  const isDanger = type === 'danger';
  return (
    <div
      style={{
        padding: 'var(--sp-3) var(--sp-4)',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${isDanger ? 'var(--color-danger-border, rgba(239,68,68,0.3))' : 'var(--color-info-border, rgba(59,130,246,0.3))'}`,
        background: isDanger ? 'var(--color-danger-bg, #fef2f2)' : 'var(--color-info-bg, #eff6ff)',
        fontSize: 'var(--font-size-sm)',
        lineHeight: 'var(--line-height-sm)',
        color: isDanger ? 'var(--color-danger-text, #991b1b)' : '#1e40af',
      }}
    >
      {message}
    </div>
  );
}

// ─── Main Component ───

export function UpdateIntakeFormDrawer({
  open,
  onOpenChange,
  brandTokens,
  questionnaire,
  initialAnswers,
  validationSchema,
  banner,
  onSave,
}: UpdateIntakeFormDrawerProps) {
  const [localAnswers, setLocalAnswers] = useState<IntakeAnswers>(initialAnswers);
  const didHydrateRef = useRef(false);

  // Hydrate once on open; don't clobber while user is editing
  useEffect(() => {
    if (open && !didHydrateRef.current) {
      setLocalAnswers(initialAnswers);
      didHydrateRef.current = true;
    }
    if (!open) didHydrateRef.current = false;
  }, [open, initialAnswers]);

  const handleSave = () => {
    onSave(localAnswers);
    onOpenChange(false);
  };

  const isSaveDisabled = banner?.type === 'danger';

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Update Intake Form"
      portalStyle={brandTokens as CSSProperties}
      badges={
        <>
          <DrawerBadge
            label={questionnaire.status === 'published' ? 'Published' : 'Draft'}
            variant={questionnaire.status === 'published' ? 'neutral' : 'warning'}
          />
          <DrawerBadge label={`v${questionnaire.version}`} variant="neutral" />
        </>
      }
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="gradient" size="lg" onClick={handleSave} disabled={isSaveDisabled}>
            Save Changes
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5, 20px)',
        }}
      >
        {banner && <IntakeBanner type={banner.type} message={banner.message} />}
        <IntakeRenderer
          questionnaire={questionnaire}
          value={localAnswers}
          onChange={setLocalAnswers}
          errors={[]}
          showFieldErrors={false}
          validationSchema={validationSchema}
        />
      </div>
    </Drawer>
  );
}
